/**
 * Altas del onboarding NUEVO (repo Cucuru-com/onboarding). API de webadmin2
 * (lambda `onboarding-v2`) → JWT post-MFA (`webadmin2.jwt`), igual que contracts.ts.
 *
 * El alta vieja (CoreDB) sigue en la pestaña Onboarding del collector.
 */
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "http://localhost:3000";

export type ObStatus = "draft" | "submitted" | "provisioning" | "provisioned" | "approved" | "rejected";
export type DocType = "id1" | "id2" | "memo" | "pow";
export type ScanStatus = "NO_THREATS_FOUND" | "THREATS_FOUND" | "UNSUPPORTED" | "ACCESS_DENIED" | "FAILED" | "PENDING";

export interface ObListItem {
  id: string; email: string; country: "ar" | "mx"; status: ObStatus;
  updated_at: string; submitted_at?: string; collector_id?: string;
  doing_business_as?: string; tax_id?: string; tax_category?: "pj" | "ph";
}

export interface ObDetail {
  id: string; email: string; country: "ar" | "mx"; status: ObStatus;
  data: Record<string, string | number | undefined>;
  bank_owner?: { id: string; name: string; account: string; checked_at: string };
  documents: { type: DocType; content_type: string; size: number; uploaded_at: string; scan: ScanStatus; download_url?: string }[];
  contract?: { version: string; accepted_at: string; html: string };
  review_note?: string; reviewed_by?: string; reviewed_at?: string;
  collector_id?: string; provisioning_error?: string; provisioned_at?: string;
  created_at: string; updated_at: string; submitted_at?: string;
  history: { at: string; by: string; action: string; note?: string }[];
  malware_scan: boolean;
}

export class ObError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message); this.name = "ObError";
  }
}

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...authHeader(), ...(init.headers ?? {}) } });
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ObError(res.status, body?.error ?? "error", body?.message ?? body?.error ?? `HTTP ${res.status}`, body?.details);
  return body as T;
}

const post = <T,>(path: string, body: unknown) =>
  call<T>(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

export const listOnboardings = (status: ObStatus) =>
  call<{ items: ObListItem[] }>(`/onboarding-v2?status=${status}`).then(r => r.items);
export const getOnboarding = (id: string) => call<ObDetail>(`/onboarding-v2/${encodeURIComponent(id)}`);
export const approveOnboarding = (id: string, collectorName?: string) =>
  post<{ status: ObStatus }>(`/onboarding-v2/${encodeURIComponent(id)}/approve`, collectorName ? { collector_name: collectorName } : {});
export const requestChanges = (id: string, note: string) => post<{ status: ObStatus }>(`/onboarding-v2/${encodeURIComponent(id)}/request-changes`, { note });
export const rejectOnboarding = (id: string, note: string) => post<{ status: ObStatus }>(`/onboarding-v2/${encodeURIComponent(id)}/reject`, { note });

export const DOC_LABELS: Record<DocType, string> = {
  id1: "DNI frente", id2: "DNI dorso", memo: "Estatuto / contrato social", pow: "Poder del representante",
};

export const STATUS_LABELS: Record<ObStatus, string> = {
  draft: "Borrador", submitted: "Para revisar", provisioning: "Creando collector",
  provisioned: "Collector creado", approved: "Aprobado (alta manual)", rejected: "Rechazado",
};

export { suggestedCollectorName } from "../lib/collectorName";
